import { FavoritStern } from '../favoriten/Favoriten';
import React, { useMemo, useState } from 'react';
import { ArrowUpRight, GitMerge, MessageSquare, Phone, PhoneOff, Plus, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import type { TrialRequestItem } from '../../lib/trialRequests';
import { formatDateTime } from '../../lib/dateFormat';

type Props = {
  item: TrialRequestItem;
  fachName?: string | null;
  dashboardCompact?: boolean;
  onOpenPersonAction: () => void;
  onOpenMessage?: () => void;
  onOpenHistory: () => void;
  onCall: () => void;
  onCallFailed: () => void;
  onCommunicate: () => void;
  onOpenMerge: () => void;
  showMergeAction: boolean;
  onConvert: () => void;
  canConvert: boolean;
  onHide: () => void;
  /** Nur bei ausgeblendeten Anfragen: „Wieder einblenden“ (T-0063). */
  onUnhide?: () => void;
  /** Vormerken/abnehmen; fehlt der Handler, gibt es den Stern nicht (T-0013). */
  onToggleFavorit?: (favorit: boolean) => void;
  onDelete?: () => void;
  onAddQuickNote: (text: string) => Promise<void> | void;
};

export default function TrialRequestGridCard({
  item,
  fachName,
  dashboardCompact = false,
  onOpenPersonAction,
  onOpenMessage,
  onOpenHistory,
  onCall,
  onCallFailed,
  onCommunicate,
  onOpenMerge,
  showMergeAction,
  onConvert,
  canConvert,
  onHide,
  onUnhide,
  onToggleFavorit,
  onDelete,
  onAddQuickNote,
}: Readonly<Props>) {
  const [note, setNote] = useState('');

  const hasMessage = useMemo(() => {
    return !!((item.bevorzugte_zeiten || '').trim() || (item.notizen || '').trim());
  }, [item.bevorzugte_zeiten, item.notizen]);

  const subjectLabel = (fachName || '').trim();
  const latestEventText = (item.last_history_description || '').trim() || 'Anfrage eingegangen';
  const latestEventAt = item.last_history_created_at || item.created_at;
  const latestEventDetails = (item.last_history_details || '').trim();
  const statusKey = String(item.status || '').toLowerCase();
  // „In Bearbeitung“ setzt das Verknüpfen von selbst. Als Badge las es sich wie
  // „da ist schon jemand dran“ — und hielt Kollegen vom Bearbeiten ab (T-0303).
  // Wer sich kümmert, zeigt der Favoriten-Stern.
  const statusLabel =
    statusKey === 'neu'
      ? 'Neu'
      : statusKey === 'in_bearbeitung'
        ? ''
        : statusKey === 'konvertiert'
          ? 'Konvertiert'
          : statusKey === 'ausgeblendet'
            ? 'Ausgeblendet'
            : item.status;

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const favoriten = item.favoriten ?? [];
  const istFavorit = !!item.ist_favorit;
  // Die Namen stehen neben dem Stern — das ist der Zweck der Anzeige: im Team
  // sehen, wer sich schon um die Anfrage kümmert (T-0014).

  return (
    <div
      className={cn(
        'bg-card text-card-foreground rounded-2xl shadow-sm border border-border p-4',
        'grid gap-4 items-start',
        // Dashboard-Compact bleibt bewusst einspaltig, um Overflow in schmalen Widgets zu vermeiden.
        dashboardCompact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-trialRequestCard'
      )}
    >
      {/* Customer */}
      <div className="space-y-2">
        {onToggleFavorit || favoriten.length > 0 ? (
          <FavoritStern istFavorit={istFavorit} favoriten={favoriten} onToggle={onToggleFavorit} />
        ) : null}
        {subjectLabel ? (
          <span className="inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
            {subjectLabel}
          </span>
        ) : null}
        {statusLabel ? (
          <Badge
            variant="secondary"
            className={cn(
              'w-fit',
              statusKey === 'konvertiert' && 'bg-emerald-100 text-emerald-800'
            )}
          >
            {statusLabel}
          </Badge>
        ) : null}

        <div className="space-y-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-semibold leading-tight hover:underline"
            onClick={(e) => {
              stop(e);
              onOpenPersonAction();
            }}
          >
            <span>{item.vorname} {item.nachname}</span>
            {item.existing_person_source === 'linked_account' && item.existing_person_id ? (
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Plus className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>{item.email}</div>
            <div>{item.telefon || '-'}</div>
          </div>
        </div>

        {item.existing_person ? (
          <Badge variant="success" className="w-fit">
            {item.existing_person_source === 'linked_account'
              ? 'Mit bestehendem Datensatz verknüpft'
              : 'E-Mail bereits vorhanden'}
          </Badge>
        ) : null}

        {/* Wunschzeiten und Nachricht direkt auf der Karte, je zwei Zeilen;
            der ganze Text steht im Tooltip (T-0162). */}
        {(item.bevorzugte_zeiten || '').trim() ? (
          <div className="text-xs" title={item.bevorzugte_zeiten || ''}>
            <span className="font-semibold text-muted-foreground">Wunschzeiten: </span>
            <span className="line-clamp-2 whitespace-pre-line">{item.bevorzugte_zeiten}</span>
          </div>
        ) : null}
        {(item.notizen || '').trim() ? (
          <div className="text-xs" title={item.notizen || ''}>
            <span className="font-semibold text-muted-foreground">Nachricht: </span>
            <span className="line-clamp-2 whitespace-pre-line">{item.notizen}</span>
          </div>
        ) : null}

        {hasMessage ? (
          <button
            type="button"
            className="text-xs underline text-muted-foreground hover:text-foreground w-fit"
            onClick={(e) => {
              stop(e);
              onOpenMessage?.();
            }}
          >
            <span className="font-semibold">Nachricht öffnen</span>
          </button>
        ) : null}
      </div>

      {/* Contact icons */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">Kontakt</div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            onClick={(e) => {
              stop(e);
              onCall();
            }}
            title="Anrufen"
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg text-destructive"
            onClick={(e) => {
              stop(e);
              onCallFailed();
            }}
            title="Nicht erreicht"
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            onClick={(e) => {
              stop(e);
              onCommunicate();
            }}
            title="Nachricht senden (E-Mail/SMS)"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Events + note */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">Ereignisse</div>
        <button
          type="button"
          className={cn(
            'w-full text-left rounded-lg bg-muted px-3 py-2 text-xs',
            'hover:bg-muted/80'
          )}
          onClick={(e) => {
            stop(e);
            onOpenHistory();
          }}
        >
          <div className="text-muted-foreground">
            Klicken, um Historie zu öffnen
          </div>
          {latestEventAt ? (
            <div className="mt-1 text-foreground">
              {latestEventText} · {formatDateTime(latestEventAt)}
            </div>
          ) : null}
          {latestEventDetails ? (
            <div className="mt-1 line-clamp-3 whitespace-pre-line text-foreground/80">
              {latestEventDetails}
            </div>
          ) : null}
        </button>

        <div className="flex gap-2 items-center">
          <Input
            className="max-w-[320px] flex-1"
            placeholder="Kommentar hinzufügen"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onClick={stop}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                const text = note.trim();
                if (!text) return;
                void Promise.resolve(onAddQuickNote(text)).then(() => setNote(''));
              }
            }}
          />
          <Button
            type="button"
            className="h-10 w-10 p-0 rounded-lg"
            onClick={(e) => {
              stop(e);
              const text = note.trim();
              if (!text) return;
              void Promise.resolve(onAddQuickNote(text)).then(() => setNote(''));
            }}
            title="Kommentar speichern"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        {showMergeAction ? (
          <Button
            type="button"
            variant="secondary"
            className="justify-start"
            onClick={(e) => {
              stop(e);
              onOpenMerge();
            }}
          >
            <GitMerge className="h-4 w-4 mr-2" />
            Datensatz verknüpfen
          </Button>
        ) : null}
        <Button
          type="button"
          className="justify-start"
          onClick={(e) => {
            stop(e);
            onConvert();
          }}
          disabled={!canConvert}
        >
          <Plus className="h-4 w-4 mr-2" />
          Probestunde anlegen
        </Button>
        {item.status === 'ausgeblendet' && onUnhide ? (
          <Button
            type="button"
            variant="secondary"
            className="justify-start"
            onClick={(e) => {
              stop(e);
              onUnhide();
            }}
          >
            Wieder einblenden
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="justify-start"
            onClick={(e) => {
              stop(e);
              onHide();
            }}
            disabled={item.status === 'ausgeblendet'}
          >
            Anfrage ausblenden
          </Button>
        )}
        {onDelete ? (
          <Button
            type="button"
            variant="ghost"
            className="justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={(e) => {
              stop(e);
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Anfrage löschen
          </Button>
        ) : null}
      </div>
    </div>
  );
}


